import { useState, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Search, Loader2, X, Star, Briefcase } from 'lucide-react';
import { useSearchJobs } from '../hooks/useCandidateJobs';
import type { CandidateJob } from '../types';
import type { JobSearchResult } from '../services/candidate-jobs.service';

interface JobSelectorProps {
  /** Jobs actuellement associés au candidat */
  selectedJobs: CandidateJob[];
  /** Callback quand un job est ajouté */
  onAdd: (jobId: number) => void;
  /** Callback quand un job est retiré */
  onRemove: (jobId: number) => void;
  /** Callback quand un job est marqué comme principal */
  onSetPrimary: (jobId: number) => void;
  /** État désactivé */
  disabled?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant de sélection de métiers avec recherche autocomplete
 * dans la nomenclature Activity > SubActivity > Job
 */
export function JobSelector({
  selectedJobs,
  onAdd,
  onRemove,
  onSetPrimary,
  disabled = false,
  className,
}: JobSelectorProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: searchResults = [], isLoading } = useSearchJobs(debouncedQuery);

  const selectedJobIds = new Set(selectedJobs.map((j) => j.jobId));

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setDebouncedQuery(value.trim());
      }, 300);

      if (value.trim().length >= 2) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    },
    [],
  );

  const handleSelect = useCallback(
    (job: JobSearchResult) => {
      if (!selectedJobIds.has(job.id)) {
        onAdd(job.id);
      }
      setInputValue('');
      setDebouncedQuery('');
      setOpen(false);
      inputRef.current?.focus();
    },
    [onAdd, selectedJobIds],
  );

  // Grouper les résultats par activité
  const groupedResults = searchResults
    .filter((r) => !selectedJobIds.has(r.id))
    .reduce<Record<string, JobSearchResult[]>>((acc, job) => {
      const key = job.activityName;
      if (!acc[key]) acc[key] = [];
      acc[key].push(job);
      return acc;
    }, {});

  return (
    <div className={cn('space-y-3', className)}>
      {/* Chips des jobs sélectionnés */}
      {selectedJobs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedJobs.map((job) => (
            <Badge
              key={job.jobId}
              variant={job.isPrimary ? 'default' : 'secondary'}
              className="flex items-center gap-1 py-1 px-2"
            >
              {job.isPrimary && <Star className="h-3 w-3 fill-current" />}
              <span className="text-xs">{job.jobName}</span>
              {job.activityName && (
                <span className="text-xs opacity-60">({job.activityName})</span>
              )}
              {!disabled && (
                <>
                  {!job.isPrimary && (
                    <button
                      type="button"
                      onClick={() => onSetPrimary(job.jobId)}
                      className="ml-1 hover:text-yellow-500"
                      title="Définir comme métier principal"
                    >
                      <Star className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemove(job.jobId)}
                    className="ml-1 hover:text-destructive"
                    title="Retirer ce métier"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Input de recherche avec autocomplete */}
      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Rechercher un métier..."
                className="pl-9"
                disabled={disabled}
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
          </PopoverAnchor>

          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[300px] overflow-y-auto"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {Object.keys(groupedResults).length === 0 && !isLoading && debouncedQuery.length >= 2 && (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Aucun métier trouvé pour "{debouncedQuery}"
              </div>
            )}

            {Object.entries(groupedResults).map(([activityName, jobs]) => (
              <div key={activityName}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {activityName}
                </div>
                {jobs.map((job) => (
                  <button
                    key={job.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2"
                    onClick={() => handleSelect(job)}
                  >
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{job.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {job.subActivityName}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/**
 * Version read-only pour affichage dans la page détail
 */
export function JobBadges({ jobs }: { jobs: CandidateJob[] }) {
  if (jobs.length === 0) {
    return <span className="text-sm text-muted-foreground">Aucun métier associé</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {jobs.map((job) => (
        <Badge
          key={job.jobId}
          variant={job.isPrimary ? 'default' : 'outline'}
          className="flex items-center gap-1"
        >
          {job.isPrimary && <Star className="h-3 w-3 fill-current" />}
          <span>{job.jobName}</span>
          {job.confidence !== null && job.confidence < 1 && (
            <span className="text-xs opacity-50">
              {Math.round(job.confidence * 100)}%
            </span>
          )}
        </Badge>
      ))}
    </div>
  );
}
